import { z } from 'zod';
import { IdSchema, MoneySchema } from './common.js';
import { CreateProductBodySchema } from './catalog.js';

const NoteSchema = z.string().trim().max(500).default('');

// ----- Solicitudes de compra y autorizaciones (Etapa 2) -----
export const PurchaseStatusSchema = z.enum(['PENDING', 'AUTHORIZED', 'REJECTED', 'RECEIVED']);

/** Producto propuesto por el local. No crea nada: solo se materializa al autorizar. */
export const ProposedProductSchema = z.object({
  name: z.string().trim().min(1).max(120),
  brand: z.string().trim().max(60).default(''),
  category: z.string().trim().max(60).default(''),
  origin: z.string().trim().max(40).default(''),
  line: z.string().trim().max(60).default(''),
});

export const PurchaseRequestSchema = z.object({
  document_id: z.number().int(),
  doc_no: z.string(),
  status: PurchaseStatusSchema,
  location_id: z.number().int(),
  location_code: z.string(),
  location_name: z.string(),
  request_type: z.enum(['EXISTING', 'NEW_PRODUCT']),
  variant_id: z.number().int().nullable(),
  target_variant_id: z.number().int().nullable(),
  ref: z.string().nullable(),
  product_name: z.string().nullable(),
  color: z.string().nullable(),
  size: z.string().nullable(),
  proposed: z.record(z.string(), z.unknown()),
  qty: z.number().int(),
  estimated_cost: z.number(),
  note: z.string(),
  requested_by: z.number().int(),
  requested_by_name: z.string().nullable(),
  created_at: z.string(),
  decided_by: z.number().int().nullable(),
  decided_by_name: z.string().nullable(),
  decided_at: z.string().nullable(),
  decision_note: z.string(),
  received_document_id: z.number().int().nullable(),
  received_doc_no: z.string().nullable(),
  received_qty: z.number().int().nullable(),
  received_cost: z.number().nullable(),
  received_by: z.number().int().nullable(),
  received_by_name: z.string().nullable(),
  received_at: z.string().nullable(),
});
export type PurchaseRequest = z.infer<typeof PurchaseRequestSchema>;

export const PurchaseQuerySchema = z.object({
  status: PurchaseStatusSchema.optional(),
  location_id: IdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export const CreatePurchaseRequestBodySchema = z
  .object({
    location_id: IdSchema,
    /** Referencia existente… */
    variant_id: IdSchema.optional(),
    /** …o producto nuevo por autorizar. */
    proposed: ProposedProductSchema.optional(),
    qty: z.number().int().min(1).max(100_000),
    estimated_cost: MoneySchema.default(0),
    note: NoteSchema,
  })
  .refine((b) => !!b.variant_id !== !!b.proposed, 'VALIDATION_FAILED');
export type CreatePurchaseRequestBody = z.input<typeof CreatePurchaseRequestBodySchema>;

/** Al autorizar un producto propuesto hay que asignarle referencia y precios. */
export const DecidePurchaseBodySchema = z.object({
  approve: z.boolean(),
  note: NoteSchema,
  product: CreateProductBodySchema.partial({ ref: true }).extend({ ref: z.string().trim().regex(/^[A-Za-z0-9._/-]{1,30}$/) }).optional(),
});
export type DecidePurchaseBody = z.input<typeof DecidePurchaseBodySchema>;

export const ReceivePurchaseBodySchema = z.object({
  qty: z.number().int().min(1).max(100_000),
  unit_cost: MoneySchema.default(0),
  supplier: z.string().trim().max(120).default(''),
  note: NoteSchema,
});
export type ReceivePurchaseBody = z.input<typeof ReceivePurchaseBodySchema>;

export const DecideResultSchema = z.object({
  document_id: z.number().int(),
  doc_no: z.string(),
  status: z.string(),
  product_id: z.number().int().nullable().optional(),
  variant_id: z.number().int().nullable().optional(),
});
